
import React, { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase/client';
import {
  ArrowBendDownRight, CaretDown, DownloadSimple, EnvelopeSimple, File as FileIcon, FilePdf, Globe, Image as ImageIcon, LockSimple, NoteBlank,
  PaperPlaneTilt, PencilSimple, Sparkle, SpinnerGap, Trash, X,
} from '@phosphor-icons/react';
import { BestaetigenDialog } from '../../../components/immobilien/ui';
import { schalteChecklistenZeile } from '../../../lib/tickets/checklistenZeile';
import { verlaufZeit } from '../../../lib/tickets/darstellung';
import type { Ticket } from './types';
import { formatTime, getHistorieInfo, type HistorieEintrag } from './TicketKommentare';
import { dekodiereHtmlEntities } from '../../../lib/emails/emailHtml';
import { ladeAnhangBlob } from '../../../lib/emails/anhangDatei';
import { toEmpfaengerListe } from '../../../lib/emails/empfaenger';
import EmailHtmlAnsicht from '../emails/EmailHtmlAnsicht';
import { zaehleChecklistenFortschritt, istChecklistenUeberschrift } from '../../../lib/tickets/checklistenVorlagen';

// ─── Types ───────────────────────────────────────────────────────────────────
// Selects identisch zu TicketKommentare.tsx (inkl. FK-Hints — ohne sie wirft
// PostgREST bei profiles einen "more than one relationship"-Fehler, weil
// mehrere FKs auf profiles zeigen können).

interface Kommentar {
  id: string;
  autor_id: string | null;
  inhalt: string;
  ist_intern: boolean;
  parent_id: string | null;
  created_at: string;
  bearbeitet_am?: string | null;
  autor?: { full_name: string; avatar_url: string | null } | null;
  /** #564 T3: aus dem Portal geschrieben bzw. im Portal geantwortet */
  portal_von?: 'mieter' | 'eigentuemer' | null;
  portal_an?: 'mieter' | 'eigentuemer' | null;
}

const PORTAL_NAME = { mieter: 'Mieterportal', eigentuemer: 'Eigentümerportal' } as const;
/** Kennzeichnung im Verlauf: Portal-Nachrichten sehen Mieter bzw. Eigentümer, Hausmeister nicht. */
function kommentarTag(k: Kommentar): string {
  if (k.portal_von) return `Aus dem ${PORTAL_NAME[k.portal_von]}`;
  if (k.portal_an) return `Im ${PORTAL_NAME[k.portal_an]} geantwortet`;
  return k.ist_intern ? 'Interne Notiz' : k.parent_id ? 'Antwort' : 'Kommentar';
}

// Anhänge der Quell-Mail (entity_links ticket→email 'quelle') — z. B. das "(s. Anhang)"-Foto,
// aus dem das Ticket entstanden ist.
interface QuellAnhang {
  id: string;
  dateiname: string;
  dateigroesse: number | null;
  dateityp: string | null;
}

// Weitere per entity_links ('quelle') verknüpfte Mails — manuell zugeordnet oder
// automatisch (gleiche Graph-Konversation im Sync). Die Ursprungs-Mail selbst
// steckt bereits in der Beschreibung und wird hier NICHT wiederholt.
interface VerknuepfteMail {
  id: string;
  von: string;
  betreff: string | null;
  datum: string | null;
  richtung: string | null;
  an: unknown;
  cc: unknown;
  bcc: unknown;
  text_inhalt: string | null;
  html_inhalt: string | null;
  anhaenge: QuellAnhang[];
}

// Empfänger-Zeilen (An/Cc/Bcc) einer verknüpften Mail; leere Felder entfallen.
const renderEmpfaengerZeilen = (m: VerknuepfteMail) => {
  const zeilen: { label: string; werte: string[] }[] = [
    { label: 'An', werte: toEmpfaengerListe(m.an) },
    { label: 'Cc', werte: toEmpfaengerListe(m.cc) },
    { label: 'Bcc', werte: toEmpfaengerListe(m.bcc) },
  ].filter((z) => z.werte.length > 0);
  if (zeilen.length === 0) return null;
  return (
    <div className="flex flex-col" style={{ gap: 2, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>
      {zeilen.map((z) => (
        <div key={z.label} className="flex items-baseline" style={{ gap: 8 }}>
          <span className="shrink-0" style={{ width: 28, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink3)' }}>{z.label}</span>
          <span className="min-w-0 break-words" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>{z.werte.join(', ')}</span>
        </div>
      ))}
    </div>
  );
};

const formatGroesse = (b: number) =>
  b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

// Datei-Typ-Icon für die Anhang-Kachel (Bild blau, PDF rot, sonst grau).
const anhangIcon = (a: QuellAnhang) => {
  const typ = (a.dateityp || '').toLowerCase();
  const name = (a.dateiname || '').toLowerCase();
  const istBild = typ.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic)$/.test(name);
  const istPdf = typ === 'application/pdf' || name.endsWith('.pdf');
  if (istBild) return { farbe: 'var(--info)', icon: <ImageIcon size={16} weight="fill" /> };
  if (istPdf) return { farbe: 'var(--bad)', icon: <FilePdf size={16} weight="fill" /> };
  return { farbe: 'var(--ink2)', icon: <FileIcon size={16} weight="fill" /> };
};

export interface TicketVerlaufThreadProps {
  ticket: Ticket;                 // liefert beschreibung, created_at, erstellt_von
  userProfile?: any;
  /** KI-Zusammenfassung (Handoff §4): steht direkt nach der Beschreibung */
  kiZusammenfassung?: ReactNode;
}

/** Initialen-Kreis neben der Blase. */
function Avatar({ name, farbe }: { name: string; farbe?: string }) {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  const ini = teile.length > 1 ? `${teile[0]![0]}${teile[teile.length - 1]![0]}` : (teile[0] ?? '?').slice(0, 2);
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center self-end" style={{
      width: 30, height: 30, borderRadius: '50%', fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
      background: farbe ?? 'var(--acc-soft)', color: farbe ? '#fff' : 'var(--acc)',
    }}>{ini}</span>
  );
}

/** Eine Verlaufs-Blase: Kopf (Art-Chip · Name · Zeit), darunter die Fläche. */
export function VerlaufBlase({ tag, tagIcon, tagFarbe, name, zeit, avatar, flaeche, rand, children, rechts }: {
  tag: string; tagIcon?: ReactNode; tagFarbe: string; name: string; zeit?: string; avatar: ReactNode;
  flaeche: string; rand?: string; children: ReactNode; rechts?: ReactNode;
}) {
  return (
    <div className="flex min-w-0" style={{ gap: 10 }}>
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 5, maxWidth: 640 }}>
        <div className="flex min-w-0 flex-wrap items-center" style={{ gap: 7 }}>
          <span className="inline-flex shrink-0 items-center" style={{ height: 22, padding: '0 8px', gap: 5, borderRadius: 11, fontSize: 11, fontWeight: 800,
            background: `color-mix(in srgb, ${tagFarbe} 14%, transparent)`, color: tagFarbe }}>{tagIcon}{tag}</span>
          <span className="truncate" style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink2)' }}>{name}</span>
          {zeit && <span className="shrink-0" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{zeit}</span>}
          {rechts && <span className="ml-auto shrink-0">{rechts}</span>}
        </div>
        <div className="min-w-0" style={{ borderRadius: '6px 20px 20px 20px', padding: '12px 14px', background: flaeche, boxShadow: rand ? `inset 0 0 0 1px ${rand}` : 'inset 0 0 0 1px var(--line)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

type ThreadEintrag =
  | { typ: 'beschreibung'; ts: string }
  | { typ: 'kommentar'; ts: string; kommentar: Kommentar }
  | { typ: 'email'; ts: string; mail: VerknuepfteMail }
  | { typ: 'audit'; ts: string; historie: HistorieEintrag };

// Checkbox-Listen wie in TicketKommentare. Mit onToggle (Beschreibung: Checklisten-
// Vorlagen abarbeiten) sind die Kästchen klickbar, sonst reine Anzeige.
function renderInhalt(inhalt: string, onToggle?: (idx: number, checked: boolean) => void) {
  // E-Mail-stämmige Texte (KI-Posteingang) enthalten rohe HTML-Entities (&nbsp;, &#65279;…)
  // — für die Plain-Text-Anzeige dekodieren.
  const dekodiert = dekodiereHtmlEntities(inhalt);
  // Abschnitts-Überschriften ("1. …" / "# …") nur im Checklisten-Kontext fetten —
  // normale Kommentare mit nummerierten Aufzählungen bleiben unverändert.
  const hatCheckliste = dekodiert.includes('☐ ') || dekodiert.includes('☑ ');
  return dekodiert.split('\n').map((line, idx) => {
    if (line.startsWith('☐ ') || line.startsWith('☑ ')) {
      const checked = line.startsWith('☑ ');
      return (
        <label key={idx} className="flex items-start" style={{ gap: 8, padding: '3px 0', cursor: onToggle ? 'pointer' : 'default' }}>
          <input
            type="checkbox"
            checked={checked}
            disabled={!onToggle}
            onChange={onToggle ? (e) => onToggle(idx, e.target.checked) : undefined}
            style={{ marginTop: 3, accentColor: 'var(--ok)', width: 15, height: 15 }}
          />
          <span style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.45, color: checked ? 'var(--ink3)' : 'var(--ink)', textDecoration: checked ? 'line-through' : 'none' }}>
            {line.slice(2)}
          </span>
        </label>
      );
    }
    if (hatCheckliste && istChecklistenUeberschrift(line)) {
      return (
        <p key={idx} className="first:pt-0" style={{ margin: 0, fontSize: 14, fontWeight: 800, padding: '10px 0 4px', color: 'var(--ink)' }}>
          {line.startsWith('# ') ? line.slice(2) : line}
        </p>
      );
    }
    if (!line.trim()) return <div key={idx} style={{ height: 10 }} />;
    return <p key={idx} className="whitespace-pre-wrap break-words" style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.5, color: 'var(--ink)' }}>{line}</p>;
  });
}

// ─── Component ─────────────────────────────────────────────────────────────────

/** Kleiner runder Knopf im Blasen-Kopf (Notiz bearbeiten/löschen). */
function KopfKnopf({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} data-tip={label} className="inline-flex items-center justify-center"
      style={{ width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
      {children}
    </button>
  );
}

export default function TicketVerlaufThread({ ticket, userProfile, kiZusammenfassung }: TicketVerlaufThreadProps) {
  const [kommentare, setKommentare] = useState<Kommentar[]>([]);
  const [historie, setHistorie] = useState<HistorieEintrag[]>([]);
  const [erstellerName, setErstellerName] = useState('');
  // Beschreibung lokal — Checklisten-Toggles (☐↔☑) speichern direkt und sollen ohne
  // Parent-Refetch sofort sichtbar sein.
  const [beschreibungLokal, setBeschreibungLokal] = useState(ticket.beschreibung ?? '');
  useEffect(() => { setBeschreibungLokal(ticket.beschreibung ?? ''); }, [ticket.id, ticket.beschreibung]);
  const [loading, setLoading] = useState(true);
  const [quellAnhaenge, setQuellAnhaenge] = useState<QuellAnhang[]>([]);
  const [verknuepfteMails, setVerknuepfteMails] = useState<VerknuepfteMail[]>([]);
  const [mailOffen, setMailOffen] = useState<Set<string>>(new Set());
  const [anhangLaedt, setAnhangLaedt] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<{ url: string; name: string; typ: string | null } | null>(null);
  // Notiz bearbeiten/löschen (#526) — nur eigene Notizen.
  const [bearbeitung, setBearbeitung] = useState<{ id: string; text: string } | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [loeschKandidat, setLoeschKandidat] = useState<Kommentar | null>(null);
  const [loescht, setLoescht] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      supabase
        .from('ticket_kommentare')
        .select('id, autor_id, inhalt, ist_intern, parent_id, created_at, bearbeitet_am, portal_von, portal_an, autor:profiles!ticket_kommentare_autor_id_fkey(full_name, avatar_url)')
        .eq('ticket_id', ticket.id)
        .order('created_at', { ascending: true }),
      supabase
        .from('ticket_historie')
        .select('id, benutzer_id, aktion, alter_wert, neuer_wert, created_at, benutzer:profiles!ticket_historie_benutzer_id_fkey(full_name)')
        .eq('ticket_id', ticket.id)
        .order('created_at', { ascending: true }),
    ]).then(([komRes, histRes]) => {
      if (cancelled) return;
      setKommentare((komRes.data as unknown as Kommentar[]) || []);
      setHistorie((histRes.data as unknown as HistorieEintrag[]) || []);
      setLoading(false);
    });

    if (ticket.erstellt_von) {
      supabase
        .from('profiles')
        .select('full_name')
        .eq('id', ticket.erstellt_von)
        .single()
        .then(({ data }) => { if (!cancelled) setErstellerName(data?.full_name || 'Unbekannt'); });
    } else {
      setErstellerName('System');
    }

    // ALLE per 'quelle' verknüpften Mails laden: die Ursprungs-Mail liefert nur ihre
    // Anhänge (Inhalt = Beschreibung), weitere Mails erscheinen als eigene Thread-Einträge.
    (async () => {
      const { data: links } = await supabase
        .from('entity_links').select('target_id')
        .eq('source_type', 'ticket').eq('source_id', ticket.id)
        .eq('target_type', 'email').eq('link_type', 'quelle');
      const emailIds = (links || []).map((l) => l.target_id as string);
      if (!emailIds.length) { if (!cancelled) { setQuellAnhaenge([]); setVerknuepfteMails([]); } return; }
      const [mailRes, anhRes] = await Promise.all([
        supabase
          .from('emails')
          .select('id, von, betreff, datum, richtung, an, cc, bcc, text_inhalt, html_inhalt')
          .in('id', emailIds)
          .order('datum', { ascending: true }),
        supabase
          .from('email_anhaenge')
          .select('id, dateiname, dateigroesse, dateityp, email_id')
          .in('email_id', emailIds),
      ]);
      if (cancelled) return;
      const anhaengeByMail = new Map<string, QuellAnhang[]>();
      for (const a of ((anhRes.data as (QuellAnhang & { email_id: string })[]) || [])) {
        const liste = anhaengeByMail.get(a.email_id) ?? [];
        liste.push(a);
        anhaengeByMail.set(a.email_id, liste);
      }
      const alle = (mailRes.data as Omit<VerknuepfteMail, 'anhaenge'>[]) || [];
      // Ursprungs-Mail NUR über quelle_email_id (steckt in der Beschreibung, nicht doppeln).
      // Manuell erstellte Tickets haben keine Quell-Mail — dort sind ALLE verknüpften
      // Mails eigene Thread-Einträge (sonst verschluckt der Skip die erste Mail).
      const quellId = ticket.quelle_email_id && emailIds.includes(ticket.quelle_email_id)
        ? ticket.quelle_email_id
        : null;
      setQuellAnhaenge(quellId ? (anhaengeByMail.get(quellId) ?? []) : []);
      setVerknuepfteMails(
        alle
          .filter((m) => m.id !== quellId)
          .map((m) => ({ ...m, anhaenge: anhaengeByMail.get(m.id) ?? [] })),
      );
    })();

    return () => { cancelled = true; };
  }, [ticket.id, ticket.erstellt_von, ticket.quelle_email_id]);

  // Klick öffnet die VORSCHAU (iframe/Bild im Overlay); Download gibt's im Overlay-Kopf.
  // Laden über denselben Weg wie das E-Mail-Modul (Storage bzw. Graph on-demand).
  const handleAnhangOeffnen = async (a: QuellAnhang) => {
    if (anhangLaedt) return;
    setAnhangLaedt(a.id);
    try {
      const blob = await ladeAnhangBlob(a.id, a.dateityp);
      const url = URL.createObjectURL(blob);
      setVorschau({ url, name: a.dateiname, typ: a.dateityp || blob.type || null });
    } catch (e) {
      console.error('Anhang konnte nicht geladen werden', (e as Error).message);
    } finally {
      setAnhangLaedt(null);
    }
  };

  // Checklisten-Punkt in der Beschreibung abhaken — Muster aus TicketKommentare
  // .handleToggleCheckbox, nur auf tickets.beschreibung. Zeilenindex bezieht sich auf
  // die ROHEN Zeilen (dekodiereHtmlEntities ändert die Zeilenanzahl nicht).
  const handleBeschreibungToggle = async (lineIdx: number, checked: boolean) => {
    const neu = schalteChecklistenZeile(beschreibungLokal, lineIdx, checked);
    if (neu === null) return;
    setBeschreibungLokal(neu);
    const { error } = await supabase.from('tickets').update({ beschreibung: neu }).eq('id', ticket.id);
    if (error) {
      console.error('Checklisten-Toggle konnte nicht gespeichert werden', error.message);
      setBeschreibungLokal(beschreibungLokal);
    }
  };

  // Kästchen in einer Notiz abhaken (#526) — jeder Mitarbeiter darf, wie bei der Beschreibung.
  const handleKommentarToggle = async (k: Kommentar, lineIdx: number, checked: boolean) => {
    const neu = schalteChecklistenZeile(k.inhalt, lineIdx, checked);
    if (neu === null) return;
    setKommentare((prev) => prev.map((x) => (x.id === k.id ? { ...x, inhalt: neu } : x)));
    const { error } = await supabase.from('ticket_kommentare').update({ inhalt: neu }).eq('id', k.id);
    if (error) {
      setKommentare((prev) => prev.map((x) => (x.id === k.id ? { ...x, inhalt: k.inhalt } : x)));
      setFehler('Haken konnte nicht gespeichert werden.');
    }
  };

  const handleKommentarSpeichern = async () => {
    if (!bearbeitung || !bearbeitung.text.trim() || speichert) return;
    setSpeichert(true);
    const jetzt = new Date().toISOString();
    const text = bearbeitung.text.trim();
    const { error } = await supabase.from('ticket_kommentare').update({ inhalt: text, bearbeitet_am: jetzt }).eq('id', bearbeitung.id);
    setSpeichert(false);
    if (error) { setFehler('Notiz konnte nicht gespeichert werden.'); return; }
    setKommentare((prev) => prev.map((x) => (x.id === bearbeitung.id ? { ...x, inhalt: text, bearbeitet_am: jetzt } : x)));
    setBearbeitung(null);
  };

  const handleKommentarLoeschen = async (k: Kommentar) => {
    setLoescht(true);
    const { error } = await supabase.from('ticket_kommentare').delete().eq('id', k.id);
    setLoescht(false);
    setLoeschKandidat(null);
    if (error) { setFehler('Notiz konnte nicht gelöscht werden.'); return; }
    setKommentare((prev) => prev.filter((x) => x.id !== k.id));
    // Im Verlauf bleibt ein Hinweis „Kommentar gelöscht“ stehen (wie in TicketKommentare).
    const { data } = await supabase.from('ticket_historie').insert({
      ticket_id: ticket.id, benutzer_id: userProfile?.id ?? null, aktion: 'kommentar_geloescht', neuer_wert: 'Kommentar gelöscht',
    }).select('id, benutzer_id, aktion, alter_wert, neuer_wert, created_at, benutzer:profiles!ticket_historie_benutzer_id_fkey(full_name)').single();
    if (data) setHistorie((prev) => [...prev, data as unknown as HistorieEintrag]);
  };

  const toggleMail = (id: string) =>
    setMailOffen((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  // Anhang-Kacheln (Beschreibung + verknüpfte Mails teilen sich das Markup).
  const renderAnhangKacheln = (anhaenge: QuellAnhang[]) => (
    <div className="flex flex-wrap" style={{ gap: 8, marginTop: 10 }}>
      {anhaenge.map((a) => {
        const ic = anhangIcon(a);
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => handleAnhangOeffnen(a)}
            data-tip="Anhang öffnen"
            className="immo-kachel-knopf flex items-center text-left"
            style={{ gap: 8, padding: '6px 12px 6px 6px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit' }}
          >
            <span className="flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: `color-mix(in srgb, ${ic.farbe} 15%, transparent)`, color: ic.farbe }}>
              {anhangLaedt === a.id ? <SpinnerGap size={14} className="animate-spin" /> : ic.icon}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate" style={{ maxWidth: 200, fontSize: 12.5, fontWeight: 800 }}>{a.dateiname}</span>
              {a.dateigroesse ? <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{formatGroesse(a.dateigroesse)}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );

  const schliesseVorschau = () => {
    if (vorschau) URL.revokeObjectURL(vorschau.url);
    setVorschau(null);
  };

  const downloadVorschau = () => {
    if (!vorschau) return;
    const el = document.createElement('a');
    el.href = vorschau.url;
    el.download = vorschau.name;
    document.body.appendChild(el);
    el.click();
    el.remove();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '32px 0', color: 'var(--ink3)' }}>
        <SpinnerGap size={20} className="animate-spin" />
      </div>
    );
  }

  // ── Zeitleiste mergen ──────────────────────────────────────────────────────
  // ticket_historie-Einträge mit aktion 'kommentar' fallen weg — die Kommentare
  // selbst erscheinen bereits als eigene Bubble (sonst doppelt), analog zum
  // Verlauf-Modus in TicketKommentare.
  //
  // Reihenfolge (Ticket #501, Björn 15.10.2026): das NEUESTE oben. Die Beschreibung
  // bleibt davor stehen — sie sagt, worum es im Ticket geht, und gehört nicht als
  // ältester Eintrag ans Ende eines langen Verlaufs.
  const eintraege: ThreadEintrag[] = [
    { typ: 'beschreibung', ts: ticket.created_at } as ThreadEintrag,
    ...([
    ...kommentare.map((k): ThreadEintrag => ({ typ: 'kommentar', ts: k.created_at, kommentar: k })),
    ...verknuepfteMails.map((m): ThreadEintrag => ({ typ: 'email', ts: m.datum ?? ticket.created_at, mail: m })),
    ...historie
      .filter(h => h.aktion !== 'kommentar')
      .map((h): ThreadEintrag => ({ typ: 'audit', ts: h.created_at, historie: h })),
    ] as ThreadEintrag[]).sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()),
  ];

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      {fehler && (
        <div role="alert" className="flex items-center" style={{ gap: 8, padding: '8px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer' }}><X size={13} weight="bold" /></button>
        </div>
      )}
      {eintraege.map((eintrag) => {
        if (eintrag.typ === 'beschreibung') {
          const fortschritt = zaehleChecklistenFortschritt(beschreibungLokal);
          const name = erstellerName || 'Unbekannt';
          return (
            <React.Fragment key="beschreibung">
              <VerlaufBlase tag="Beschreibung" tagIcon={<NoteBlank size={11} weight="fill" />} tagFarbe="var(--ink2)" name={name}
                zeit={verlaufZeit(ticket.created_at)} avatar={<Avatar name={name} />} flaeche="var(--fill)"
                rechts={fortschritt.gesamt > 0 ? (
                  <span style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800, lineHeight: '22px', display: 'inline-block',
                    background: fortschritt.erledigt === fortschritt.gesamt ? 'var(--ok-soft)' : 'var(--fill2)', color: fortschritt.erledigt === fortschritt.gesamt ? 'var(--ok)' : 'var(--ink2)' }}>
                    ☑ {fortschritt.erledigt}/{fortschritt.gesamt}
                  </span>
                ) : undefined}>
                <div className="flex flex-col">
                  {beschreibungLokal
                    ? renderInhalt(beschreibungLokal, handleBeschreibungToggle)
                    : <p style={{ margin: 0, fontSize: 14, fontStyle: 'italic', color: 'var(--ink3)' }}>Keine Beschreibung</p>}
                </div>
                {quellAnhaenge.length > 0 && renderAnhangKacheln(quellAnhaenge)}
              </VerlaufBlase>
              {kiZusammenfassung && (
                <VerlaufBlase tag="KI-Zusammenfassung" tagIcon={<Sparkle size={11} weight="fill" />} tagFarbe="#8a63d2" name="KI-Assistent" zeit="automatisch"
                  avatar={<span aria-hidden className="inline-flex shrink-0 items-center justify-center self-end" style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(160deg,#c49bff,#8a63d2)', color: '#fff' }}><Sparkle size={15} weight="fill" /></span>}
                  flaeche="color-mix(in srgb, #8a63d2 9%, var(--bg))" rand="color-mix(in srgb, #8a63d2 28%, transparent)">
                  {kiZusammenfassung}
                </VerlaufBlase>
              )}
            </React.Fragment>
          );
        }

        if (eintrag.typ === 'email') {
          const m = eintrag.mail;
          const offen = mailOffen.has(m.id);
          const istAusgang = m.richtung === 'ausgang';
          return (
            <VerlaufBlase key={`m-${m.id}`} tag={istAusgang ? 'Gesendet' : 'E-Mail'} tagFarbe={istAusgang ? 'var(--ok)' : 'var(--info)'}
              tagIcon={istAusgang ? <PaperPlaneTilt size={11} weight="fill" /> : <EnvelopeSimple size={11} weight="fill" />}
              name={m.von || 'Unbekannt'} zeit={m.datum ? verlaufZeit(m.datum) : undefined}
              avatar={<Avatar name={m.von || '?'} farbe={istAusgang ? 'var(--ok)' : 'var(--info)'} />}
              flaeche={istAusgang ? 'color-mix(in srgb, var(--acc) 8%, var(--bg))' : 'var(--fill)'}>
              <button type="button" onClick={() => toggleMail(m.id)} aria-expanded={offen}
                className="flex w-full items-center text-left" style={{ gap: 8, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 800 }}>{m.betreff || '(Kein Betreff)'}</span>
                {m.anhaenge.length > 0 && <span className="shrink-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>📎 {m.anhaenge.length}</span>}
                <CaretDown size={13} weight="bold" color="var(--ink3)" style={{ transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }} />
              </button>
              {offen && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                  {/* Empfänger — bei Sammelanfragen stehen die Betriebe im Bcc; ohne diese
                      Zeilen ließe sich später nicht mehr nachvollziehen, wer angeschrieben wurde. */}
                  {renderEmpfaengerZeilen(m)}
                  {m.html_inhalt ? (
                    <div data-theme="light" style={{ borderRadius: 12, overflow: 'hidden', background: '#fff', color: '#1b2433' }}>
                      <EmailHtmlAnsicht html={m.html_inhalt} emailId={m.id} />
                    </div>
                  ) : m.text_inhalt ? (
                    <div className="flex flex-col">{renderInhalt(m.text_inhalt)}</div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 14, fontStyle: 'italic', color: 'var(--ink3)' }}>Kein Inhalt vorhanden</p>
                  )}
                  {m.anhaenge.length > 0 && renderAnhangKacheln(m.anhaenge)}
                </div>
              )}
            </VerlaufBlase>
          );
        }

        if (eintrag.typ === 'kommentar') {
          const k = eintrag.kommentar;
          const name = k.autor?.full_name || 'Unbekannt';
          const eigene = !!userProfile?.id && k.autor_id === userProfile.id;
          const imEdit = bearbeitung?.id === k.id;
          return (
            <VerlaufBlase key={`k-${k.id}`}
              tag={kommentarTag(k)}
              tagIcon={k.portal_von || k.portal_an ? <Globe size={11} weight="fill" /> : k.ist_intern ? <LockSimple size={11} weight="fill" /> : k.parent_id ? <ArrowBendDownRight size={11} weight="bold" /> : undefined}
              tagFarbe={k.portal_von || k.portal_an ? 'var(--ok)' : k.ist_intern ? 'var(--warn)' : 'var(--hl)'}
              name={name} zeit={verlaufZeit(k.created_at) + (k.bearbeitet_am ? ' · bearbeitet' : '')} avatar={<Avatar name={name} />}
              flaeche={k.portal_von || k.portal_an ? 'color-mix(in srgb, var(--ok) 9%, var(--fill2))' : k.ist_intern ? 'color-mix(in srgb, var(--warn) 9%, var(--fill2))' : 'color-mix(in srgb, var(--hl) 8%, var(--fill))'}
              rechts={eigene && !imEdit ? (
                <span className="inline-flex" style={{ gap: 4 }}>
                  <KopfKnopf label="Notiz bearbeiten" onClick={() => setBearbeitung({ id: k.id, text: k.inhalt })}><PencilSimple size={12} weight="bold" /></KopfKnopf>
                  <KopfKnopf label="Notiz löschen" onClick={() => setLoeschKandidat(k)}><Trash size={12} weight="bold" /></KopfKnopf>
                </span>
              ) : undefined}>
              {imEdit ? (
                <div className="flex flex-col" style={{ gap: 8 }}>
                  <textarea value={bearbeitung.text} autoFocus aria-label="Notiz bearbeiten"
                    rows={Math.min(16, Math.max(3, bearbeitung.text.split('\n').length + 1))}
                    onChange={(e) => setBearbeitung({ id: k.id, text: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setBearbeitung(null);
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void handleKommentarSpeichern(); }
                    }}
                    style={{ width: '100%', resize: 'vertical', padding: '8px 10px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 14, lineHeight: 1.45, fontFamily: 'inherit' }} />
                  <div className="flex justify-end" style={{ gap: 6 }}>
                    <button type="button" onClick={() => setBearbeitung(null)} disabled={speichert}
                      style={{ height: 32, padding: '0 14px', borderRadius: 16, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit' }}>Abbrechen</button>
                    <button type="button" onClick={() => void handleKommentarSpeichern()} disabled={speichert || !bearbeitung.text.trim()}
                      style={{ height: 32, padding: '0 14px', borderRadius: 16, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 800, background: 'var(--acc)', color: '#fff', fontFamily: 'inherit', opacity: speichert || !bearbeitung.text.trim() ? 0.6 : 1 }}>
                      {speichert ? 'Speichert …' : 'Speichern'}</button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">{renderInhalt(k.inhalt, (idx, checked) => void handleKommentarToggle(k, idx, checked))}</div>
              )}
            </VerlaufBlase>
          );
        }

        // audit
        const h = eintrag.historie;
        const info = getHistorieInfo(h);
        return (
          <div key={`h-${h.id}`} className="flex justify-center">
            <div className="inline-flex max-w-full flex-wrap items-center justify-center" style={{ gap: 6, padding: '5px 12px', borderRadius: 14, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              <span aria-hidden className="inline-flex" style={{ opacity: 0.8 }}>{info.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>{info.label}</span>
              {info.detail && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>· {info.detail}</span>}
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink3)' }}>
                {h.benutzer?.full_name || 'System'} · {verlaufZeit(h.created_at)}
              </span>
            </div>
          </div>
        );
      })}

      {loeschKandidat && (
        <BestaetigenDialog titel="Notiz löschen" icon={Trash} bestaetigenLabel="Löschen" laeuft={loescht}
          text="Die Notiz wird aus dem Ticket entfernt. Im Verlauf bleibt nur der Hinweis „Kommentar gelöscht“."
          onAbbrechen={() => setLoeschKandidat(null)}
          onBestaetigen={() => void handleKommentarLoeschen(loeschKandidat)} />
      )}

      {/* Anhang-Vorschau (Overlay): Bilder direkt, PDFs & Co. im iframe; Download im Kopf. */}
      {vorschau && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 450, background: 'rgba(10,14,22,0.5)', backdropFilter: 'blur(6px)' }} onClick={schliesseVorschau}>
          <div role="dialog" aria-modal="true" aria-label={vorschau.name} className="immo-glas-stark flex w-full flex-col overflow-hidden" style={{ maxWidth: 900, height: '85vh', borderRadius: 26, color: 'var(--ink)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex shrink-0 items-center justify-between" style={{ gap: 12, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--line)' }}>
              <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{vorschau.name}</span>
              <div className="flex shrink-0 items-center" style={{ gap: 6 }}>
                <button type="button" onClick={downloadVorschau} aria-label="Herunterladen" data-tip="Herunterladen" className="inline-flex items-center justify-center"
                  style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <DownloadSimple size={16} weight="bold" />
                </button>
                <button type="button" onClick={schliesseVorschau} aria-label="Schließen" className="inline-flex items-center justify-center"
                  style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <X size={15} weight="bold" />
                </button>
              </div>
            </div>
            {(vorschau.typ || '').startsWith('image/') ? (
              <div className="flex flex-1 items-center justify-center overflow-auto p-4" style={{ background: 'var(--fill)' }}>
                <img src={vorschau.url} alt={vorschau.name} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <iframe src={vorschau.url} title={vorschau.name} className="flex-1 w-full border-0" />
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
